import React, { useState } from 'react';
import cashReservesService from '../../services/cashReservesService';
import { REASON_LABELS } from './helpers';

// ─── review status badge ──────────────────────────────────────────────────────
export const ReviewStatusBadge = ({ account }) => {
  const status = account.review_status;
  if (status === 'reviewed') {
    return (
      <span className="badge bg-success" title={`Reviewed by ${account.last_reviewed_by || 'unknown'}`}>
        <i className="bi bi-check2-circle me-1"></i>Reviewed
      </span>
    );
  }
  if (status === 'needs_rereview') {
    const reasons = (account.rereview_reasons || []).map((r) => REASON_LABELS[r] || r).join(', ');
    return (
      <span className="badge bg-warning text-dark" title={reasons}>
        <i className="bi bi-arrow-repeat me-1"></i>Re-review
      </span>
    );
  }
  // Unreviewed is the default/quiet state — no badge (kept the "Mark Reviewed"
  // action next to it), so it doesn't add noise.
  return null;
};

// ─── mark-reviewed action ─────────────────────────────────────────────────────
export const MarkReviewedButton = ({ accountNumber, days, onReviewed, size = 'sm' }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleClick = async () => {
    setSaving(true);
    setError('');
    try {
      await cashReservesService.markAccountReviewed(accountNumber, { days });
      if (onReviewed) onReviewed();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to save review');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="d-flex align-items-center gap-2">
      {error && <span className="text-danger small">{error}</span>}
      <button className={`btn btn-${size} btn-success`} onClick={handleClick} disabled={saving}>
        <i className={`bi ${saving ? 'bi-hourglass-split' : 'bi-check2-circle'} me-1`}></i>
        {saving ? 'Saving…' : 'Mark Reviewed'}
      </button>
    </div>
  );
};

// ─── undo-review action (only for accounts that have a review) ─────────────────
export const UndoReviewButton = ({ accountNumber, onReviewed, size = 'sm' }) => {
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');

  const handleClick = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Undo the most recent review for this account? This deletes the review record.')) {
      return;
    }
    setWorking(true);
    setError('');
    try {
      await cashReservesService.undoAccountReview(accountNumber);
      if (onReviewed) onReviewed();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to undo review');
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="d-flex align-items-center gap-2">
      {error && <span className="text-danger small">{error}</span>}
      <button className={`btn btn-${size} btn-outline-danger`} onClick={handleClick} disabled={working}>
        <i className={`bi ${working ? 'bi-hourglass-split' : 'bi-arrow-counterclockwise'} me-1`}></i>
        {working ? 'Undoing…' : 'Undo Review'}
      </button>
    </div>
  );
};

/**
 * Mark every not-yet-reviewed account in a family reviewed.
 *
 * Hidden for a single account — the row's own button already covers that, and
 * two identical buttons would be noise. Runs sequentially rather than in
 * parallel: each POST recomputes a transaction summary server-side, so N
 * concurrent requests is the worst thing to hand one Lambda. Sequential also
 * gives a truthful progress label and a clean partial-failure story.
 */
export const MarkAllReviewedButton = ({ accounts, days, onReviewed }) => {
  const [progress, setProgress] = useState(0); // 0 = idle
  const [failures, setFailures] = useState([]);

  const targets = accounts.filter((a) => a.review_status !== 'reviewed');
  if (accounts.length <= 1 || targets.length === 0) return null;

  const working = progress > 0;

  const handleClick = async () => {
    if (
      // eslint-disable-next-line no-alert
      !window.confirm(
        `Mark ${targets.length} account${targets.length === 1 ? '' : 's'} in this family reviewed?`
      )
    ) {
      return;
    }
    setFailures([]);
    const failed = [];
    for (let i = 0; i < targets.length; i += 1) {
      setProgress(i + 1);
      try {
        // eslint-disable-next-line no-await-in-loop
        await cashReservesService.markAccountReviewed(targets[i].account_number, { days });
      } catch (err) {
        failed.push({
          accountNumber: targets[i].account_number,
          message: err.response?.data?.detail || err.message || 'Failed',
        });
      }
    }
    setProgress(0);
    setFailures(failed);
    // Always reload, so the accounts that did succeed are reflected.
    if (onReviewed) onReviewed();
  };

  return (
    <div className="d-flex align-items-center gap-2 flex-wrap">
      {failures.map((f) => (
        <span key={f.accountNumber} className="text-danger small">
          Could not mark {f.accountNumber} — {f.message}
        </span>
      ))}
      <button
        type="button"
        className="btn btn-sm btn-outline-success"
        onClick={handleClick}
        disabled={working}
        title="Mark every account in this family reviewed"
      >
        <i className={`bi ${working ? 'bi-hourglass-split' : 'bi-check2-all'} me-1`}></i>
        {working
          ? `Marking ${progress} of ${targets.length}…`
          : `Mark all reviewed (${targets.length})`}
      </button>
    </div>
  );
};
